import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "../../../components/Buttons/Button";
import { getOriginColor, isTextOriginPreviewKind } from "../utils/layerTree";
import { fitTextInBox } from "../instance/fitTextInBox";
import {
  isItalicTextStyle,
  isUnderlineTextStyle,
  resolveTextShadow,
} from "../utils/textStyle";
import { resolveTextOrientation } from "../utils/textOrientation";
import FittedTextPreview from "../utils/FittedTextPreview";
import { canHighlightTextSource } from "../utils/textHighlights";
import { useInlineReplacementMetrics } from "../utils/useInlineReplacementMetrics";
import ZoomControls from "./ZoomControls";
import TextCollisionAlert, {
  TextCollisionHighlight,
} from "./TextCollisionAlert";
import { findTextSlotCollisions } from "./textSlotCollisions";
import {
  CANVAS_VIEWPORT_PADDING,
  canvasZoomBoardSx,
  getCanvasZoomLayout,
  useCanvasZoom,
} from "./useCanvasZoom";
import { useFreezeViewportScroll } from "./useFreezeViewportScroll";
import SelectionChromeOverlay, {
  CANVAS_SELECTION_HANDLE_CLASS,
} from "./SelectionChromeOverlay";
import AlignmentGuidesOverlay from "./AlignmentGuidesOverlay";
import RotationAngleOverlay, {
  syncRotationAngleGuide,
} from "./RotationAngleOverlay";
import {
  isAlignmentSnapHeldOff,
  snapBoxToGuides,
  boxesOutsideDraggedBranch,
} from "./alignmentGuides";
import { pickTopmostCanvasHit } from "../../../utils/pickCanvasHit";
import {
  idsIntersectingMarquee,
  MARQUEE_MIN_SIZE_PX,
  marqueeOverlaySx,
  normalizeMarqueeRect,
} from "./canvasMarquee";
import {
  ROTATE_HANDLE_CORNER,
  cssRotateTransform,
  pointInRotatedBox,
  rotationDegreesFromDrag,
  unrotatePoint,
  boxCenter,
} from "./layerRotation";
import { resizePixelBoxFromHandle } from "./resizePixelBoxFromHandle";
import {
  canCropImageLayer,
  croppedImageSx,
  cropPixelBoxFromHandle,
  cropsEqual,
  isCropHandleGesture,
} from "./layerCrop";
import { CroppedLayerImage } from "./CroppedLayerImage";
import { useShiftHeld } from "./useShiftHeld";
import {
  getLiveMarqueeSelection,
  isAdditiveLayerSelect,
} from "./canvasLayerSelection";
import {
  GROUP_SELECTION_ID,
  axisAlignedBounds,
  groupSelectionChrome,
  scaleBoxesToBounds,
  translateBoxes,
} from "./canvasGroupTransform";
import {
  canvasArtboardSx,
  canvasWorkspaceSx,
  canvasZoomHideScrollbarsSx,
  canvasZoomViewportSx,
} from "./canvasWorkspace";
import LayerTryScanOverlay from "./layerTryScan";

const objectFitFromBox = (fit) => (fit === "cover" ? "cover" : "fill");
const MIN_LAYER_SIZE_PX = 8;
const GUIDE_OVERLAY_Z_INDEX = 19;
const NO_SEAMS = [];
const ASPECT_LOCK_CORNERS = new Set(["nw", "ne", "sw", "se"]);

const minNormSize = (boardW, boardH) =>
  MIN_LAYER_SIZE_PX /
  Math.max(1, Math.min(Number(boardW) || 1, Number(boardH) || 1));

const clamp = ({ value, min, max }) => Math.min(max, Math.max(min, value));

const boxContains = ({ box, point, artboardWidth, artboardHeight }) => {
  const boardW = Math.max(1, Number(artboardWidth) || 1);
  const boardH = Math.max(1, Number(artboardHeight) || 1);
  return pointInRotatedBox({
    point: { x: point.x * boardW, y: point.y * boardH },
    box: {
      x: box.nx * boardW,
      y: box.ny * boardH,
      width: box.nw * boardW,
      height: box.nh * boardH,
      rotation: box.rotation,
    },
  });
};

const pickBoxAtPoint = ({ boxes, point, artboardWidth, artboardHeight }) => {
  const hits = boxes
    .filter((box) => boxContains({ box, point, artboardWidth, artboardHeight }))
    .map((box) => ({
      box,
      isBackground: Boolean(box.isBackground),
      paintIndex: box.paintIndex,
      zIndex: box.zIndex,
      area: box.nw * box.nh,
    }));
  return pickTopmostCanvasHit({ hits })?.box || null;
};

const stackZ = ({ box, selected }) => {
  const paint = Number(box.paintIndex) || 0;
  const z = Number(box.zIndex) || 0;
  const area = box.nw * box.nh;
  const areaTie = Math.round((1 - area) * 100);
  return paint * 100000 + z * 1000 + areaTie + (selected ? 1 : 0);
};

const resizeFromCorner = ({ drag, point, artboardWidth, artboardHeight }) => {
  const dx = point.x - drag.startX;
  const dy = point.y - drag.startY;
  const originRight = drag.originNx + drag.originNw;
  const originBottom = drag.originNy + drag.originNh;
  const boardW = Math.max(1, Number(artboardWidth) || 1);
  const boardH = Math.max(1, Number(artboardHeight) || 1);
  const minNorm = minNormSize(boardW, boardH);
  const originNw = Math.max(drag.originNw, minNorm);
  const originNh = Math.max(drag.originNh, minNorm);

  if (ASPECT_LOCK_CORNERS.has(drag.corner)) {
    const originPxW = originNw * boardW;
    const originPxH = originNh * boardH;
    const originPxX = drag.originNx * boardW;
    const originPxY = drag.originNy * boardH;
    const originRightPx = originPxX + originPxW;
    const originBottomPx = originPxY + originPxH;
    const cursorPxX = point.x * boardW;
    const cursorPxY = point.y * boardH;
    const fromWest = drag.corner.includes("w");
    const fromNorth = drag.corner.includes("n");
    const anchorX = fromWest ? originRightPx : originPxX;
    const anchorY = fromNorth ? originBottomPx : originPxY;
    const denom = originPxW * originPxW + originPxH * originPxH;
    const signedDelta = ({ px, py }) => {
      const signedX = fromWest ? anchorX - px : px - anchorX;
      const signedY = fromNorth ? anchorY - py : py - anchorY;
      return denom > 0
        ? (signedX * originPxW + signedY * originPxH) / denom
        : 1;
    };
    const startScale = signedDelta({
      px: drag.startX * boardW,
      py: drag.startY * boardH,
    });
    const cursorScale = signedDelta({ px: cursorPxX, py: cursorPxY });
    const minScale = Math.max(minNorm / originNw, minNorm / originNh);
    const scale = Math.max(
      minScale,
      startScale > 0 ? cursorScale / startScale : cursorScale
    );
    const nextPxW = originPxW * scale;
    const nextPxH = originPxH * scale;
    return {
      nx: fromWest ? (originRightPx - nextPxW) / boardW : drag.originNx,
      ny: fromNorth ? (originBottomPx - nextPxH) / boardH : drag.originNy,
      nw: nextPxW / boardW,
      nh: nextPxH / boardH,
    };
  }

  let nx = drag.originNx;
  let ny = drag.originNy;
  let nw = drag.originNw;
  let nh = drag.originNh;

  if (drag.corner.includes("w")) {
    nx = Math.min(drag.originNx + dx, originRight - minNorm);
    nw = originRight - nx;
  } else if (drag.corner.includes("e")) {
    nw = Math.max(minNorm, drag.originNw + dx);
  }

  if (drag.corner.includes("n")) {
    ny = Math.min(drag.originNy + dy, originBottom - minNorm);
    nh = originBottom - ny;
  } else if (drag.corner.includes("s")) {
    nh = Math.max(minNorm, drag.originNh + dy);
  }

  return { nx, ny, nw, nh };
};

const isEditableTextBox = (box) =>
  box?.type === "text" &&
  (box?.sourceKind === "fixed" || isTextOriginPreviewKind(box?.sourceKind));

const hasTextPreview = (box) =>
  box?.type === "text" && Boolean(String(box?.text || "").trim());

export const LayerTextPreview = ({
  box,
  isEditing,
  scale,
  artboardWidth,
  artboardHeight,
  opacity = 1,
  onTextCommit,
  isSelectingText = false,
  selectedPhrase = "",
  onSelectionChange,
}) => {
  const orientation = resolveTextOrientation(box.style?.orientation);
  const fontFamily = box.style?.fontAssetRef || "sans-serif";
  const fontWeight =
    Number(box.style?.fontWeight) > 0 ? Number(box.style.fontWeight) : 400;
  const requestedFontSize = Number(box.style?.fontSize);
  const hasRequestedFontSize =
    Number.isFinite(requestedFontSize) && requestedFontSize > 0;
  const italic = isItalicTextStyle(box.style?.textStyle);
  const underline = isUnderlineTextStyle(box.style?.textStyle);
  const boxWidth = (Number(box.nw) || 0) * artboardWidth;
  const boxHeight = (Number(box.nh) || 0) * artboardHeight;
  const letterSpacing = Number(box.style?.letterSpacing);
  const inlineImages = box.inlineReplacementUrls || {};
  const replacementMetrics = useInlineReplacementMetrics({
    urls: inlineImages,
  });
  // Fitting binary-searches the font size against canvas measurements, which
  // runs ~7ms on a highlighted paragraph. Keyed on its own inputs it survives
  // the re-renders that do not touch the text — zoom, selection, a slider on
  // another layer — instead of refitting every layer on the artboard.
  const fitKey = JSON.stringify({
    text: box.text,
    boxWidth,
    boxHeight,
    fontFamily,
    minimumLines: box.overflow?.minimumLines,
    maximumLines: box.overflow?.maximumLines,
    minimumFontSize: box.overflow?.minimumFontSize,
    requestedFontSize: hasRequestedFontSize ? requestedFontSize : undefined,
    lineHeightMult: box.style?.lineHeight,
    letterSpacing: Number.isFinite(letterSpacing) ? letterSpacing : undefined,
    orientation,
    italic,
    underline,
    fontWeight,
    highlights: canHighlightTextSource(box.sourceKind)
      ? box.style?.highlights
      : undefined,
    strokeWidth: Number(box.style?.strokeWidth) || 0,
    inlineReplacements: box.style?.inlineReplacements,
    replacementMetrics,
  });
  const layout = useMemo(() => fitTextInBox(JSON.parse(fitKey)), [fitKey]);

  return (
    <FittedTextPreview
      text={box.text}
      layout={layout}
      fallbackFontSize={hasRequestedFontSize ? requestedFontSize : 12}
      scale={scale}
      color={box.style?.color || "#fff"}
      fontFamily={fontFamily}
      fontWeight={fontWeight}
      align={box.style?.align || "left"}
      orientation={orientation}
      textStyle={box.style?.textStyle}
      letterSpacing={Number.isFinite(letterSpacing) ? letterSpacing : 0}
      strokeWidth={Number(box.style?.strokeWidth) || 0}
      strokeColor={box.style?.strokeColor}
      shadow={resolveTextShadow(box.style)}
      backgroundPadding={Number(box.style?.backgroundPadding) || 0}
      backgroundColor={box.style?.backgroundColor}
      backgroundRadius={box.style?.backgroundRadius}
      opacity={opacity}
      isEditing={isEditing}
      onCommit={(nextText) => onTextCommit(box.id, nextText)}
      highlights={
        canHighlightTextSource(box.sourceKind)
          ? box.style?.highlights
          : undefined
      }
      inlineReplacements={box.style?.inlineReplacements}
      replacementMetrics={replacementMetrics}
      inlineImages={inlineImages}
      isSelectingText={isSelectingText}
      selectedPhrase={selectedPhrase}
      onSelectionChange={onSelectionChange}
    />
  );
};

const canSelectTextInBox = (box) =>
  box?.type === "text" && canHighlightTextSource(box?.sourceKind);

const TemplateCanvasBox = memo(function TemplateCanvasBox({
  box,
  selected,
  isEditingText,
  isSelectingText = false,
  selectedPhrase = "",
  textScale,
  artboardWidth,
  artboardHeight,
  isGenerating = false,
  onTextCommit,
  onTextSelectionChange,
}) {
  const color = getOriginColor({
    sourceKind: box.sourceKind,
    type: box.type,
  });
  const showAsset = Boolean(box.assetUrl);
  const assetOpacity = Number.isFinite(Number(box.assetOpacity))
    ? Number(box.assetOpacity)
    : 1;
  const showText = hasTextPreview(box) || isEditingText;
  const textOpacity = Number.isFinite(Number(box.textOpacity))
    ? Number(box.textOpacity)
    : 1;
  return (
    <Box
      data-template-box-id={box.id}
      sx={{
        position: "absolute",
        left: `${box.nx * 100}%`,
        top: `${box.ny * 100}%`,
        width: `${box.nw * 100}%`,
        height: `${box.nh * 100}%`,
        zIndex: stackZ({ box, selected }),
        border: "2px solid transparent",
        pointerEvents: "auto",
        overflow: "visible",
        transform: cssRotateTransform(box.rotation),
        transformOrigin: "center center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          // The 2px transparent border of the outer box eats 4px of the inner
          // width; text is fitted to the full box width, so it gets it back.
          inset: box.type === "text" ? "-2px" : 0,
          // Text clips itself, wide enough to keep a highlight pill whole where
          // it hugs past the layer box; anything else stays bound to the box.
          overflow: box.type === "text" ? "visible" : "hidden",
          backgroundColor: showAsset || showText ? "transparent" : `${color}1A`,
          pointerEvents: isEditingText || isSelectingText ? "auto" : "none",
          userSelect: isSelectingText ? "text" : undefined,
          cursor: isSelectingText ? "text" : undefined,
        }}
      >
        {showAsset ? (
          <CroppedLayerImage
            src={box.assetUrl}
            crop={box.crop}
            objectFit={objectFitFromBox(box.fit)}
            opacity={assetOpacity}
            markForLiveCrop
          />
        ) : null}
        {showText ? (
          <LayerTextPreview
            box={box}
            isEditing={isEditingText}
            scale={textScale}
            artboardWidth={artboardWidth}
            artboardHeight={artboardHeight}
            opacity={isEditingText ? 1 : textOpacity}
            onTextCommit={onTextCommit}
            isSelectingText={isSelectingText}
            selectedPhrase={selectedPhrase}
            onSelectionChange={(state) =>
              onTextSelectionChange?.(box.id, state)
            }
          />
        ) : null}
        {isGenerating ? <LayerTryScanOverlay /> : null}
      </Box>
    </Box>
  );
});

const TemplateCanvasOverlay = ({
  imageUrl,
  showGuide = true,
  artboardWidth,
  artboardHeight,
  boxes = [],
  tryingNodeIDs = {},
  selectedID,
  selectedIDs = [],
  editingTextID = null,
  onTextSelectionChange,
  selectedTextNodeID = null,
  selectedTextPhrase = "",
  isDrawing,
  isLocked,
  isScanning = false,
  analysisError = "",
  retryLabel = "",
  onRetry,
  onSelectBox,
  onCreateBox,
  onUpdateBox,
  onUpdateBoxes,
  onEndBoxTransform,
  onStartTextEdit,
  onTextCommit,
  alignmentGuidesEnabled = true,
  guideOpacity = 0,
  onSelectIds,
  seamOffsets = NO_SEAMS,
  canvasApiRef = null,
}) => {
  const boardRef = useRef(null);
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const previewRef = useRef(null);
  const layoutRef = useRef(null);
  const rootRef = useRef(null);
  const alignmentGuidesRef = useRef(null);
  const rotationAngleRef = useRef(null);
  const snapContextRef = useRef({
    getOtherBoxes: () => [],
    artboardWidth: 0,
    artboardHeight: 0,
    minSize: 8,
  });
  const onUpdateBoxRef = useRef(onUpdateBox);
  const onUpdateBoxesRef = useRef(onUpdateBoxes);
  const onEndBoxTransformRef = useRef(onEndBoxTransform);
  const artboardRef = useRef({ artboardWidth, artboardHeight });
  onUpdateBoxRef.current = onUpdateBox;
  onUpdateBoxesRef.current = onUpdateBoxes;
  onEndBoxTransformRef.current = onEndBoxTransform;
  artboardRef.current = { artboardWidth, artboardHeight };
  const [draft, setDraft] = useState(null);
  const [collisionListOpen, setCollisionListOpen] = useState(false);
  const [activeCollisionKey, setActiveCollisionKey] = useState(null);
  const [marquee, setMarquee] = useState(null);
  const marqueeRef = useRef(null);
  const [isTransforming, setIsTransforming] = useState(false);
  const [isCropDrag, setIsCropDrag] = useState(false);
  const [liveCropPreview, setLiveCropPreview] = useState(null);
  const shiftHeld = useShiftHeld();
  const { zoom, viewportSize, onZoomIn, onZoomOut, onFit, centerOn } =
    useCanvasZoom({
      viewportRef,
      boardRef,
      resetKey: `${artboardWidth}x${artboardHeight}`,
      lockRef: dragRef,
    });
  const slideZoneCount = seamOffsets.length + 1;
  useEffect(() => {
    if (!canvasApiRef) return undefined;
    canvasApiRef.current = {
      centerOnSlide: ({ index }) =>
        centerOn({
          nextZoom: slideZoneCount,
          ratioX: (index + 0.5) / slideZoneCount,
        }),
    };
    return () => {
      canvasApiRef.current = null;
    };
  }, [canvasApiRef, centerOn, slideZoneCount]);
  useFreezeViewportScroll({
    viewportRef,
    frozen: isTransforming || Boolean(marquee),
  });

  const getPoint = ({ event, clampToBoard = false }) => {
    const board = boardRef.current;
    if (!board) return { x: 0, y: 0 };
    const rect = board.getBoundingClientRect();
    if (!rect.width || !rect.height) return { x: 0, y: 0 };
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    if (!clampToBoard) {
      return { x, y };
    }
    return {
      x: clamp({ value: x, min: 0, max: 1 }),
      y: clamp({ value: y, min: 0, max: 1 }),
    };
  };

  const isPointOnBoard = (point) =>
    point.x >= 0 && point.x <= 1 && point.y >= 0 && point.y <= 1;

  const captureLayout = () => {
    const board = boardRef.current;
    const root = rootRef.current;
    const overlay = root?.querySelector("[data-template-chrome-root]");
    if (!board || !overlay) return;
    const boardRect = board.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    layoutRef.current = {
      boardLeft: boardRect.left,
      boardTop: boardRect.top,
      boardWidth: boardRect.width,
      boardHeight: boardRect.height,
      frameLeft: boardRect.left - overlayRect.left,
      frameTop: boardRect.top - overlayRect.top,
    };
  };

  const getPointFromLayout = (event) => {
    const layout = layoutRef.current;
    if (!layout?.boardWidth || !layout?.boardHeight) {
      return getPoint({ event });
    }
    return {
      x: (event.clientX - layout.boardLeft) / layout.boardWidth,
      y: (event.clientY - layout.boardTop) / layout.boardHeight,
    };
  };

  const snapLiveBox = ({ nextBox, drag, event }) => {
    const ctx = snapContextRef.current;
    if (!ctx.artboardWidth || !ctx.artboardHeight) {
      drag.snap = null;
      alignmentGuidesRef.current?.clear();
      return nextBox;
    }
    const snapMode =
      drag.mode === "resize" || drag.mode === "rotate" ? drag.mode : "move";
    const pixelBox = {
      x: nextBox.nx * ctx.artboardWidth,
      y: nextBox.ny * ctx.artboardHeight,
      width: nextBox.nw * ctx.artboardWidth,
      height: nextBox.nh * ctx.artboardHeight,
      rotation: nextBox.rotation,
    };
    const result = snapBoxToGuides({
      box: pixelBox,
      mode: snapMode,
      corner: drag.corner,
      otherBoxes: ctx.getOtherBoxes(drag.id),
      artboardWidth: ctx.artboardWidth,
      artboardHeight: ctx.artboardHeight,
      prevSnap: drag.snap,
      minSize: ctx.minSize,
      disabled: !alignmentGuidesEnabled || isAlignmentSnapHeldOff(event),
    });
    drag.snap = result.snap;
    alignmentGuidesRef.current?.setGuides(result.guides);
    return {
      ...nextBox,
      nx: result.box.x / ctx.artboardWidth,
      ny: result.box.y / ctx.artboardHeight,
      nw: result.box.width / ctx.artboardWidth,
      nh: result.box.height / ctx.artboardHeight,
    };
  };

  const applyLiveBox = (next) => {
    const board = boardRef.current;
    const root = rootRef.current;
    const layout = layoutRef.current;
    const transform = cssRotateTransform(next.rotation);
    const layer = board?.querySelector(`[data-template-box-id="${next.id}"]`);
    if (layer) {
      layer.style.left = `${next.nx * 100}%`;
      layer.style.top = `${next.ny * 100}%`;
      layer.style.width = `${next.nw * 100}%`;
      layer.style.height = `${next.nh * 100}%`;
      layer.style.transform = transform;
      const cropImg = layer.querySelector("[data-template-crop-img]");
      if (cropImg && Object.prototype.hasOwnProperty.call(next, "crop")) {
        const cropSx = croppedImageSx({ crop: next.crop });
        cropImg.style.right = "auto";
        cropImg.style.bottom = "auto";
        cropImg.style.left =
          typeof cropSx.left === "number" ? `${cropSx.left}px` : cropSx.left;
        cropImg.style.top =
          typeof cropSx.top === "number" ? `${cropSx.top}px` : cropSx.top;
        cropImg.style.width = cropSx.width;
        cropImg.style.height = cropSx.height;
        cropImg.style.maxWidth = "none";
        cropImg.style.maxHeight = "none";
        cropImg.style.inset = "";
      }
    }
    const chrome = root?.querySelector(
      `[data-template-chrome-id="${next.id}"]`
    );
    if (chrome && layout) {
      chrome.style.left = `${layout.frameLeft + next.nx * layout.boardWidth}px`;
      chrome.style.top = `${layout.frameTop + next.ny * layout.boardHeight}px`;
      chrome.style.width = `${Math.max(0, next.nw * layout.boardWidth)}px`;
      chrome.style.height = `${Math.max(0, next.nh * layout.boardHeight)}px`;
      const rotated = chrome.querySelector("[data-template-chrome-rotate]");
      if (rotated) rotated.style.transform = transform;
    }
  };

  const applyLiveBoxes = (nextBoxes) => {
    nextBoxes.forEach((next) => applyLiveBox(next));
    const layout = layoutRef.current;
    const root = rootRef.current;
    if (!layout || nextBoxes.length < 2) return;
    const bounds = axisAlignedBounds(
      nextBoxes.map((box) => ({
        x: box.nx * layout.boardWidth,
        y: box.ny * layout.boardHeight,
        width: box.nw * layout.boardWidth,
        height: box.nh * layout.boardHeight,
      }))
    );
    const chrome = root?.querySelector(
      `[data-template-chrome-id="${GROUP_SELECTION_ID}"]`
    );
    if (!chrome || !bounds) return;
    chrome.style.left = `${layout.frameLeft + bounds.x}px`;
    chrome.style.top = `${layout.frameTop + bounds.y}px`;
    chrome.style.width = `${Math.max(0, bounds.width)}px`;
    chrome.style.height = `${Math.max(0, bounds.height)}px`;
  };

  const startBoxesDrag = ({ mode, movingBoxes, point, corner }) => {
    if (!movingBoxes.length) return;
    const origins = {};
    movingBoxes.forEach((item) => {
      origins[item.id] = {
        nx: item.nx,
        ny: item.ny,
        nw: item.nw,
        nh: item.nh,
        rotation: Number(item.rotation) || 0,
      };
    });
    const primary = movingBoxes[0];
    dragRef.current = {
      mode,
      corner,
      id: primary.id,
      ids: movingBoxes.map((item) => item.id),
      origins,
      originBounds: axisAlignedBounds(
        movingBoxes.map((item) => ({
          x: item.nx,
          y: item.ny,
          width: item.nw,
          height: item.nh,
        }))
      ),
      startX: point.x,
      startY: point.y,
      originNx: primary.nx,
      originNy: primary.ny,
      originNw: primary.nw,
      originNh: primary.nh,
      originRotation: Number(primary.rotation) || 0,
      originCrop: primary.crop,
    };
    setIsCropDrag(mode === "crop");
    setLiveCropPreview(null);
    previewRef.current = {
      boxes: movingBoxes.map((item) => ({
        id: item.id,
        nx: item.nx,
        ny: item.ny,
        nw: item.nw,
        nh: item.nh,
        rotation: Number(item.rotation) || 0,
      })),
    };
    captureLayout();
    setIsTransforming(true);
  };

  const clearLiveStyles = (id) => {
    const board = boardRef.current;
    const root = rootRef.current;
    const layer = board?.querySelector(`[data-template-box-id="${id}"]`);
    if (layer) {
      layer.style.left = "";
      layer.style.top = "";
      layer.style.width = "";
      layer.style.height = "";
      layer.style.transform = "";
      const cropImg = layer.querySelector("[data-template-crop-img]");
      if (cropImg) {
        cropImg.style.left = "";
        cropImg.style.top = "";
        cropImg.style.right = "";
        cropImg.style.bottom = "";
        cropImg.style.width = "";
        cropImg.style.height = "";
        cropImg.style.inset = "";
      }
    }
    const chrome = root?.querySelector(`[data-template-chrome-id="${id}"]`);
    if (chrome) {
      chrome.style.left = "";
      chrome.style.top = "";
      chrome.style.width = "";
      chrome.style.height = "";
      const rotated = chrome.querySelector("[data-template-chrome-rotate]");
      if (rotated) rotated.style.transform = "";
    }
  };

  const onPointerDownBoard = useCallback(
    (event) => {
      if (isLocked) return;
      const point = getPoint({ event });
      if (isDrawing) {
        if (!isPointOnBoard(point)) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDraft({
          x: point.x,
          y: point.y,
          w: 0,
          h: 0,
          originX: point.x,
          originY: point.y,
        });
        return;
      }
      const box = pickBoxAtPoint({
        boxes,
        point,
        artboardWidth,
        artboardHeight,
      });
      if (!box) {
        onTextSelectionChange?.();
        return;
      }
      const currentSelected = selectedIDs.length
        ? selectedIDs
        : selectedID
          ? [selectedID]
          : [];
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      onSelectBox(box.id, event);
      if (isAdditiveLayerSelect(event)) return;
      if (box.isBackground || box.locked) return;
      if (editingTextID === box.id) return;
      const selectedMoving = boxes.filter(
        (item) =>
          currentSelected.includes(item.id) &&
          !item.isBackground &&
          !item.locked
      );
      const moving =
        currentSelected.includes(box.id) && selectedMoving.length > 1
          ? selectedMoving
          : [box];
      startBoxesDrag({ mode: "move", movingBoxes: moving, point });
    },
    // eslint-disable-next-line
    [
      artboardHeight,
      artboardWidth,
      boxes,
      editingTextID,
      isDrawing,
      isLocked,
      onSelectBox,
      selectedID,
      selectedIDs,
    ]
  );

  const onDoubleClickBoard = useCallback(
    (event) => {
      if (isLocked || isDrawing || typeof onStartTextEdit !== "function") {
        return;
      }
      const point = getPoint({ event });
      const box = pickBoxAtPoint({
        boxes,
        point,
        artboardWidth,
        artboardHeight,
      });
      if (!box || box.locked || !isEditableTextBox(box)) return;
      onSelectBox(box.id);
      onStartTextEdit(box.id);
    },
    [
      artboardHeight,
      artboardWidth,
      boxes,
      isDrawing,
      isLocked,
      onSelectBox,
      onStartTextEdit,
    ]
  );

  const onPointerMoveBoard = useCallback(
    (event) => {
      if (!draft) return;
      const point = getPoint({ event, clampToBoard: true });
      const x = Math.min(draft.originX, point.x);
      const y = Math.min(draft.originY, point.y);
      setDraft({
        ...draft,
        x,
        y,
        w: Math.abs(point.x - draft.originX),
        h: Math.abs(point.y - draft.originY),
      });
    },
    [draft]
  );

  const onPointerUpBoard = useCallback(() => {
    if (!draft) return;
    if (
      draft.w >= minNormSize(artboardWidth, artboardHeight) &&
      draft.h >= minNormSize(artboardWidth, artboardHeight)
    ) {
      onCreateBox({ nx: draft.x, ny: draft.y, nw: draft.w, nh: draft.h });
    }
    setDraft(null);
  }, [artboardHeight, artboardWidth, draft, onCreateBox]);

  const onPointerDownWorkspace = (event) => {
    if (isLocked || isDrawing) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (boardRef.current?.contains(event.target)) return;
    if (
      event.target instanceof Element &&
      event.target.closest(`.${CANVAS_SELECTION_HANDLE_CLASS}`)
    ) {
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const point = getPoint({ event });
    const next = {
      x: point.x,
      y: point.y,
      w: 0,
      h: 0,
      originX: point.x,
      originY: point.y,
      additive: isAdditiveLayerSelect(event),
    };
    marqueeRef.current = next;
    setMarquee(next);
  };

  const onPointerMoveWorkspace = (event) => {
    const current = marqueeRef.current;
    if (!current) return;
    const point = getPoint({ event });
    const next = {
      originX: current.originX,
      originY: current.originY,
      additive: isAdditiveLayerSelect(event),
      ...normalizeMarqueeRect({
        originX: current.originX,
        originY: current.originY,
        x: point.x,
        y: point.y,
      }),
    };
    marqueeRef.current = next;
    setMarquee(next);
  };

  const onPointerUpWorkspace = (event) => {
    const current = marqueeRef.current;
    marqueeRef.current = null;
    setMarquee(null);
    if (!current || typeof onSelectIds !== "function") return;
    const ids = idsIntersectingMarquee({
      boxes,
      marquee: {
        x: current.x * artboardWidth,
        y: current.y * artboardHeight,
        w: current.w * artboardWidth,
        h: current.h * artboardHeight,
      },
      toBounds: (box) => ({
        x: box.nx * artboardWidth,
        y: box.ny * artboardHeight,
        width: box.nw * artboardWidth,
        height: box.nh * artboardHeight,
        rotation: Number(box.rotation) || 0,
      }),
      skip: (box) => box.isBackground,
    });
    onSelectIds(ids, event);
  };

  const onPointerDownResize = ({ event, box, corner, movingBoxes }) => {
    const targets = movingBoxes?.length ? movingBoxes : box ? [box] : [];
    if (isLocked || isDrawing || !targets.length) return;
    if (targets.every((item) => item.isBackground || item.locked)) return;
    event.preventDefault();
    event.stopPropagation();
    const point = getPoint({ event });
    const moving = targets.filter((item) => !item.isBackground && !item.locked);
    const isCrop =
      corner !== ROTATE_HANDLE_CORNER &&
      isCropHandleGesture({
        event,
        shiftHeld,
        layer: moving[0],
        selectedCount: moving.length,
      });
    const mode =
      corner === ROTATE_HANDLE_CORNER ? "rotate" : isCrop ? "crop" : "resize";
    startBoxesDrag({
      mode,
      movingBoxes: moving,
      point,
      corner,
    });
    if (mode === "rotate") {
      syncRotationAngleGuide(rotationAngleRef, {
        degrees: Number(moving[0].rotation) || 0,
        event,
      });
    }
  };

  const onPointerMoveWindow = (event) => {
    if (!dragRef.current) return;
    const point = getPointFromLayout(event);
    const drag = dragRef.current;
    const { artboardWidth: boardW, artboardHeight: boardH } =
      artboardRef.current;
    const layout = layoutRef.current;
    const isGroup = (drag.ids || []).length > 1;

    if (isGroup && drag.mode === "move") {
      const dx = point.x - drag.startX;
      const dy = point.y - drag.startY;
      const originList = drag.ids.map((id) => ({
        id,
        x: drag.origins[id].nx,
        y: drag.origins[id].ny,
        width: drag.origins[id].nw,
        height: drag.origins[id].nh,
        rotation: drag.origins[id].rotation,
      }));
      const nextBoxes = translateBoxes({ boxes: originList, dx, dy }).map(
        (box) => ({
          id: box.id,
          nx: box.x,
          ny: box.y,
          nw: box.width,
          nh: box.height,
          rotation: box.rotation,
        })
      );
      previewRef.current = { boxes: nextBoxes };
      applyLiveBoxes(nextBoxes);
      alignmentGuidesRef.current?.clear();
      return;
    }

    if (isGroup && drag.mode === "resize" && drag.originBounds) {
      const nextBounds = resizeFromCorner({
        drag: {
          corner: drag.corner,
          startX: drag.startX,
          startY: drag.startY,
          originNx: drag.originBounds.x,
          originNy: drag.originBounds.y,
          originNw: drag.originBounds.width,
          originNh: drag.originBounds.height,
        },
        point,
        artboardWidth: boardW,
        artboardHeight: boardH,
      });
      const originList = drag.ids.map((id) => ({
        id,
        x: drag.origins[id].nx,
        y: drag.origins[id].ny,
        width: drag.origins[id].nw,
        height: drag.origins[id].nh,
        rotation: drag.origins[id].rotation,
      }));
      const nextBoxes = scaleBoxesToBounds({
        boxes: originList,
        originBounds: drag.originBounds,
        nextBounds: {
          x: nextBounds.nx,
          y: nextBounds.ny,
          width: nextBounds.nw,
          height: nextBounds.nh,
        },
        minSize: minNormSize(boardW, boardH),
      }).map((box) => ({
        id: box.id,
        nx: box.x,
        ny: box.y,
        nw: box.width,
        nh: box.height,
        rotation: box.rotation,
      }));
      previewRef.current = { boxes: nextBoxes };
      applyLiveBoxes(nextBoxes);
      alignmentGuidesRef.current?.clear();
      return;
    }

    let nextBox;
    if (drag.mode === "rotate") {
      const displayW = layout?.boardWidth || boardW;
      const displayH = layout?.boardHeight || boardH;
      nextBox = {
        nx: drag.originNx,
        ny: drag.originNy,
        nw: drag.originNw,
        nh: drag.originNh,
        rotation: rotationDegreesFromDrag({
          center: {
            x: (drag.originNx + drag.originNw / 2) * displayW,
            y: (drag.originNy + drag.originNh / 2) * displayH,
          },
          startPoint: {
            x: drag.startX * displayW,
            y: drag.startY * displayH,
          },
          currentPoint: {
            x: point.x * displayW,
            y: point.y * displayH,
          },
          originRotation: drag.originRotation,
        }),
      };
      syncRotationAngleGuide(rotationAngleRef, {
        degrees: nextBox.rotation,
        event,
      });
    } else if (drag.mode === "crop") {
      const displayW = layout?.boardWidth || boardW;
      const displayH = layout?.boardHeight || boardH;
      const originPx = {
        x: drag.originNx * displayW,
        y: drag.originNy * displayH,
        width: drag.originNw * displayW,
        height: drag.originNh * displayH,
      };
      const rotation = drag.originRotation || 0;
      const center = boxCenter(originPx);
      const startPoint = {
        x: drag.startX * displayW,
        y: drag.startY * displayH,
      };
      const currentPoint = {
        x: point.x * displayW,
        y: point.y * displayH,
      };
      const cropped = cropPixelBoxFromHandle({
        originFrame: originPx,
        originCrop: drag.originCrop,
        corner: drag.corner,
        startPoint: rotation
          ? unrotatePoint({
              point: startPoint,
              center,
              degrees: rotation,
            })
          : startPoint,
        point: rotation
          ? unrotatePoint({
              point: currentPoint,
              center,
              degrees: rotation,
            })
          : currentPoint,
        minSize: MIN_LAYER_SIZE_PX,
      });
      nextBox = {
        nx: cropped.x / displayW,
        ny: cropped.y / displayH,
        nw: cropped.width / displayW,
        nh: cropped.height / displayH,
        rotation,
        crop: cropped.crop,
      };
      const cropPreview = { id: drag.id, ...nextBox };
      previewRef.current = { boxes: [cropPreview] };
      setLiveCropPreview(cropPreview);
      alignmentGuidesRef.current?.clear();
      return;
    } else if (drag.mode === "resize") {
      const rotation = drag.originRotation || 0;
      if (!rotation) {
        nextBox = {
          ...resizeFromCorner({
            drag,
            point,
            artboardWidth: boardW,
            artboardHeight: boardH,
          }),
          rotation,
        };
      } else {
        const displayW = layout?.boardWidth || boardW;
        const displayH = layout?.boardHeight || boardH;
        const originPx = {
          x: drag.originNx * displayW,
          y: drag.originNy * displayH,
          width: drag.originNw * displayW,
          height: drag.originNh * displayH,
        };
        const center = boxCenter(originPx);
        const resized = resizePixelBoxFromHandle({
          origin: originPx,
          corner: drag.corner,
          startPoint: unrotatePoint({
            point: {
              x: drag.startX * displayW,
              y: drag.startY * displayH,
            },
            center,
            degrees: rotation,
          }),
          point: unrotatePoint({
            point: { x: point.x * displayW, y: point.y * displayH },
            center,
            degrees: rotation,
          }),
          minSize: MIN_LAYER_SIZE_PX,
        });
        nextBox = {
          nx: resized.x / displayW,
          ny: resized.y / displayH,
          nw: resized.width / displayW,
          nh: resized.height / displayH,
          rotation,
        };
      }
    } else {
      const dx = point.x - drag.startX;
      const dy = point.y - drag.startY;
      nextBox = {
        nx: drag.originNx + dx,
        ny: drag.originNy + dy,
        nw: drag.originNw,
        nh: drag.originNh,
        rotation: drag.originRotation || 0,
      };
    }
    const snapped = snapLiveBox({
      nextBox,
      drag,
      event,
    });
    const nextPreview = { id: drag.id, ...snapped };
    previewRef.current = { boxes: [nextPreview] };
    applyLiveBox(nextPreview);
  };

  const onPointerUpWindow = () => {
    if (!dragRef.current) return;
    const drag = dragRef.current;
    const liveBoxes = Array.isArray(previewRef.current?.boxes)
      ? previewRef.current.boxes
      : previewRef.current
        ? [previewRef.current]
        : [];
    const updates = liveBoxes.filter((live) => {
      const origin = drag.origins?.[live.id];
      const originNx = origin?.nx ?? drag.originNx;
      const originNy = origin?.ny ?? drag.originNy;
      const originNw = origin?.nw ?? drag.originNw;
      const originNh = origin?.nh ?? drag.originNh;
      const originRotation = origin?.rotation ?? drag.originRotation;
      const originCrop = origin?.crop ?? drag.originCrop;
      return (
        live.nx !== originNx ||
        live.ny !== originNy ||
        live.nw !== originNw ||
        live.nh !== originNh ||
        (Number(live.rotation) || 0) !== (Number(originRotation) || 0) ||
        (drag.mode === "crop" && !cropsEqual(live.crop, originCrop))
      );
    });
    if (updates.length) {
      if (typeof onUpdateBoxesRef.current === "function") {
        onUpdateBoxesRef.current(updates);
      } else {
        updates.forEach((update) => onUpdateBoxRef.current?.(update));
      }
    }
    const cropPreview =
      drag.mode === "crop"
        ? liveBoxes.find((live) => live.id === drag.id)
        : null;
    dragRef.current = null;
    previewRef.current = null;
    alignmentGuidesRef.current?.clear();
    rotationAngleRef.current?.clear();
    setLiveCropPreview(cropPreview?.id ? cropPreview : null);
    setIsTransforming(false);
    setIsCropDrag(false);
    onEndBoxTransformRef.current?.();
  };

  const onPointerMoveWindowRef = useRef(onPointerMoveWindow);
  const onPointerUpWindowRef = useRef(onPointerUpWindow);
  onPointerMoveWindowRef.current = onPointerMoveWindow;
  onPointerUpWindowRef.current = onPointerUpWindow;

  useEffect(() => {
    const onMove = (event) => {
      onPointerMoveWindowRef.current(event);
    };
    const onUp = () => {
      onPointerUpWindowRef.current();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  useLayoutEffect(() => {
    if (isTransforming) return undefined;
    boxes.forEach((box) => clearLiveStyles(box.id));
    clearLiveStyles(GROUP_SELECTION_ID);
    if (!liveCropPreview?.id) return undefined;
    const committed = boxes.find((box) => box.id === liveCropPreview.id);
    if (committed && cropsEqual(committed.crop, liveCropPreview.crop)) {
      setLiveCropPreview(null);
    }
    return undefined;
  }, [boxes, isTransforming, liveCropPreview]);

  const aspect =
    artboardWidth && artboardHeight ? artboardWidth / artboardHeight : 1;
  const { fitWidth, fitHeight } = useMemo(() => {
    const pad = CANVAS_VIEWPORT_PADDING * 2;
    const availableWidth = Math.max(1, viewportSize.width - pad);
    const availableHeight = Math.max(1, viewportSize.height - pad);
    if (availableWidth / availableHeight > aspect) {
      return {
        fitHeight: availableHeight,
        fitWidth: availableHeight * aspect,
      };
    }
    return {
      fitWidth: availableWidth,
      fitHeight: availableWidth / aspect,
    };
  }, [aspect, viewportSize.height, viewportSize.width]);
  const { boardWidth, boardHeight, padX, padY, contentWidth, contentHeight } =
    getCanvasZoomLayout({
      viewportWidth: viewportSize.width,
      viewportHeight: viewportSize.height,
      fitWidth,
      fitHeight,
      zoom,
    });
  snapContextRef.current = {
    getOtherBoxes: (excludeId) =>
      boxesOutsideDraggedBranch({ boxes, excludeId }).map((box) => ({
        x: box.nx * boardWidth,
        y: box.ny * boardHeight,
        width: box.nw * boardWidth,
        height: box.nh * boardHeight,
        rotation: Number(box.rotation) || 0,
      })),
    artboardWidth: boardWidth,
    artboardHeight: boardHeight,
    seamOffsets: seamOffsets.map(
      (offset) => (offset / artboardWidth) * boardWidth
    ),
    minSize: MIN_LAYER_SIZE_PX,
  };
  const textScale = artboardWidth > 0 ? fitWidth / artboardWidth : 1;
  const committedSelectedIDs = useMemo(() => {
    if (selectedIDs.length) return selectedIDs;
    return selectedID ? [selectedID] : [];
  }, [selectedID, selectedIDs]);
  const marqueeHitIds = useMemo(() => {
    if (!marquee) return [];
    return idsIntersectingMarquee({
      boxes,
      marquee: {
        x: marquee.x * artboardWidth,
        y: marquee.y * artboardHeight,
        w: marquee.w * artboardWidth,
        h: marquee.h * artboardHeight,
      },
      toBounds: (box) => ({
        x: box.nx * artboardWidth,
        y: box.ny * artboardHeight,
        width: box.nw * artboardWidth,
        height: box.nh * artboardHeight,
        rotation: Number(box.rotation) || 0,
      }),
      skip: (box) => box.isBackground,
    });
  }, [artboardHeight, artboardWidth, boxes, marquee]);
  const isMarqueeSelecting =
    Boolean(marquee) &&
    ((Number(marquee.w) || 0) * artboardWidth >= MARQUEE_MIN_SIZE_PX ||
      (Number(marquee.h) || 0) * artboardHeight >= MARQUEE_MIN_SIZE_PX);
  const liveSelection = getLiveMarqueeSelection({
    committedIDs: committedSelectedIDs,
    committedPrimary: selectedID,
    hitIds: marqueeHitIds,
    additive: Boolean(marquee?.additive),
    active: isMarqueeSelecting,
  });
  const selectedSet = liveSelection.ids;
  const livePrimaryID = liveSelection.primary;
  const textCollisions = useMemo(
    () => findTextSlotCollisions({ boxes }),
    [boxes]
  );
  const activeCollision =
    textCollisions.find((item) => item.key === activeCollisionKey) || null;
  useEffect(() => {
    if (!textCollisions.length) {
      setCollisionListOpen(false);
      setActiveCollisionKey(null);
      return;
    }
    if (
      activeCollisionKey &&
      !textCollisions.some((item) => item.key === activeCollisionKey)
    ) {
      setActiveCollisionKey(null);
    }
  }, [activeCollisionKey, textCollisions]);
  const displayBoxes = useMemo(() => {
    if (!liveCropPreview?.id) return boxes;
    return boxes.map((box) =>
      box.id === liveCropPreview.id
        ? {
            ...box,
            nx: liveCropPreview.nx,
            ny: liveCropPreview.ny,
            nw: liveCropPreview.nw,
            nh: liveCropPreview.nh,
            rotation: liveCropPreview.rotation,
            crop: liveCropPreview.crop,
          }
        : box
    );
  }, [boxes, liveCropPreview]);
  const selectionChrome = useMemo(() => {
    if (!boardWidth || !boardHeight) return [];
    const transformableSelected = displayBoxes.filter(
      (box) => selectedSet.includes(box.id) && !box.isBackground && !box.locked
    );
    const isGroupTransform = transformableSelected.length > 1;
    const layerChrome = displayBoxes
      .filter((box) => selectedSet.includes(box.id))
      .map((box) => {
        const isPrimary = livePrimaryID === box.id;
        return {
          id: box.id,
          x: box.nx * boardWidth,
          y: box.ny * boardHeight,
          width: box.nw * boardWidth,
          height: box.nh * boardHeight,
          rotation: Number(box.rotation) || 0,
          color: getOriginColor({
            sourceKind: box.sourceKind,
            type: box.type,
          }),
          name: box.name,
          showLabel: isPrimary && !isGroupTransform,
          showHandles:
            isPrimary &&
            !isGroupTransform &&
            !isMarqueeSelecting &&
            !isLocked &&
            !isDrawing &&
            !box.isBackground &&
            !box.locked &&
            editingTextID !== box.id,
          cropHandles:
            (isCropDrag || (!isTransforming && shiftHeld)) &&
            canCropImageLayer(box),
        };
      });
    const groupChrome = isGroupTransform
      ? groupSelectionChrome({
          bounds: axisAlignedBounds(
            transformableSelected.map((box) => ({
              x: box.nx * boardWidth,
              y: box.ny * boardHeight,
              width: box.nw * boardWidth,
              height: box.nh * boardHeight,
            }))
          ),
          showHandles: !isLocked && !isDrawing && !isMarqueeSelecting,
        })
      : null;
    return groupChrome ? [...layerChrome, groupChrome] : layerChrome;
  }, [
    boardHeight,
    boardWidth,
    displayBoxes,
    editingTextID,
    isCropDrag,
    isDrawing,
    isLocked,
    isMarqueeSelecting,
    isTransforming,
    livePrimaryID,
    selectedSet,
    shiftHeld,
  ]);

  return (
    <Box
      ref={rootRef}
      sx={{ position: "relative", height: "100%", minHeight: 0 }}
    >
      <Box
        ref={viewportRef}
        sx={{
          height: "100%",
          minHeight: 0,
          ...canvasWorkspaceSx,
          ...canvasZoomViewportSx,
          ...(zoom <= 1.001 ? canvasZoomHideScrollbarsSx : null),
          border: "1px solid #E4E4E7",
          borderRadius: "12px",
        }}
      >
        <Box
          onPointerDown={onPointerDownWorkspace}
          onPointerMove={onPointerMoveWorkspace}
          onPointerUp={onPointerUpWorkspace}
          onPointerCancel={onPointerUpWorkspace}
          sx={{
            position: "relative",
            width: contentWidth,
            height: contentHeight,
            overflow: "visible",
            touchAction: "none",
          }}
        >
          <Box
            ref={boardRef}
            onPointerDown={onPointerDownBoard}
            onDoubleClick={onDoubleClickBoard}
            onPointerMove={onPointerMoveBoard}
            onPointerUp={onPointerUpBoard}
            sx={{
              isolation: "isolate",
              borderRadius: 0,
              overflow: "visible",
              ...canvasArtboardSx,
              ...canvasZoomBoardSx({
                padX,
                padY,
                fitWidth,
                fitHeight,
                zoom,
              }),
              cursor: isLocked
                ? "not-allowed"
                : isDrawing
                  ? "crosshair"
                  : "move",
              userSelect: "none",
              touchAction: "none",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                overflow: "visible",
              }}
            >
              {displayBoxes.map((box) => (
                <TemplateCanvasBox
                  key={box.id}
                  box={box}
                  selected={selectedSet.includes(box.id)}
                  isGenerating={Boolean(tryingNodeIDs[box.id])}
                  isEditingText={editingTextID === box.id}
                  isSelectingText={
                    editingTextID === box.id && canSelectTextInBox(box)
                  }
                  selectedPhrase={
                    selectedTextNodeID === box.id ? selectedTextPhrase : ""
                  }
                  textScale={textScale}
                  artboardWidth={artboardWidth}
                  artboardHeight={artboardHeight}
                  onTextCommit={onTextCommit}
                  onTextSelectionChange={onTextSelectionChange}
                />
              ))}
              {draft ? (
                <Box
                  sx={{
                    position: "absolute",
                    left: `${draft.x * 100}%`,
                    top: `${draft.y * 100}%`,
                    width: `${draft.w * 100}%`,
                    height: `${draft.h * 100}%`,
                    border: "1.5px dashed #18181B",
                    backgroundColor: "rgba(24,24,27,.08)",
                    pointerEvents: "none",
                  }}
                />
              ) : null}
            </Box>
            {seamOffsets.map((offset) => (
              <Box
                key={`seam-${offset}`}
                aria-hidden
                data-continuous-seam=""
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `${(offset / artboardWidth) * 100}%`,
                  width: 0,
                  borderLeft: "1.5px dashed rgba(225, 29, 143, 0.75)",
                  pointerEvents: "none",
                  zIndex: GUIDE_OVERLAY_Z_INDEX + 1,
                }}
              />
            ))}
            {marquee ? (
              <Box
                sx={marqueeOverlaySx({
                  x: marquee.x * fitWidth,
                  y: marquee.y * fitHeight,
                  w: marquee.w * fitWidth,
                  h: marquee.h * fitHeight,
                })}
              />
            ) : null}
            {showGuide && imageUrl ? (
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: GUIDE_OVERLAY_Z_INDEX,
                  pointerEvents: "none",
                  opacity: guideOpacity,
                  backgroundImage: `url(${imageUrl})`,
                  backgroundSize: "100% 100%",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              />
            ) : null}
            {isScanning ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  overflow: "hidden",
                  pointerEvents: "none",
                  zIndex: 20,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    width: "30%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
                    animation: "templateScanSweep 2.2s ease-in-out infinite",
                    "@keyframes templateScanSweep": {
                      "0%": { transform: "translateX(-120%)" },
                      "100%": { transform: "translateX(430%)" },
                    },
                  }}
                />
              </Box>
            ) : null}
            {analysisError ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 21,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  bgcolor: "rgba(15,15,15,0.55)",
                  px: 2,
                }}
              >
                <Typography
                  sx={{ color: "#fff", textAlign: "center", fontWeight: 500 }}
                >
                  {analysisError}
                </Typography>
                {onRetry ? (
                  <Button color="secondary" variant="filled" onClick={onRetry}>
                    {retryLabel}
                  </Button>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
      <TextCollisionHighlight
        boardRef={boardRef}
        viewportRef={viewportRef}
        syncKey={`${zoom}-${boardWidth}-${boardHeight}-${activeCollisionKey || ""}`}
        polygon={collisionListOpen ? activeCollision?.polygon : null}
      />
      <SelectionChromeOverlay
        boardRef={boardRef}
        viewportRef={viewportRef}
        selections={selectionChrome}
        syncKey={`${zoom}-${boardWidth}-${boardHeight}-${selectedSet.join(",")}`}
        onHandlePointerDown={({ event, id, corner }) => {
          if (id === GROUP_SELECTION_ID) {
            const moving = boxes.filter(
              (item) => selectedSet.includes(item.id) && !item.isBackground
            );
            onPointerDownResize({ event, corner, movingBoxes: moving });
            return;
          }
          const box = boxes.find((item) => item.id === id);
          if (!box) return;
          onPointerDownResize({ event, box, corner });
        }}
      />
      <AlignmentGuidesOverlay
        ref={alignmentGuidesRef}
        boardRef={boardRef}
        viewportRef={viewportRef}
        syncKey={`${zoom}-${boardWidth}-${boardHeight}`}
        clipToCanvas
      />
      <RotationAngleOverlay ref={rotationAngleRef} />
      <ZoomControls
        zoom={zoom}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onFit={onFit}
      />
      <TextCollisionAlert
        collisions={textCollisions}
        open={collisionListOpen}
        activeKey={activeCollisionKey}
        onToggle={() => {
          setCollisionListOpen((current) => {
            if (current) setActiveCollisionKey(null);
            return !current;
          });
        }}
        onClose={() => {
          setCollisionListOpen(false);
          setActiveCollisionKey(null);
        }}
        onSelect={(collision) => {
          setActiveCollisionKey(collision.key);
          onSelectIds?.([collision.sourceID, collision.targetID], {
            shiftKey: false,
            metaKey: false,
            ctrlKey: false,
          });
        }}
      />
    </Box>
  );
};

export default TemplateCanvasOverlay;
